// src/pages/comercial/components/CriarObjetivosDialogEquipas.js
import React, { useEffect, useMemo, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function onlyNumberOrEmpty(v) {
  const s = (v ?? "").toString().trim();
  if (!s) return "";
  // aceita números com vírgula/ponto
  const norm = s.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(norm)) return s; // deixa escrever, valida no aplicar
  return norm;
}

export default function CriarObjetivosDialogEquipas({
  open,
  onClose,
  onApply,

  // draft atual (pode ser null)
  value,

  // opções de vendedores [{ value, label }]
  vendedoresOptions = [],

  // critérios ativos (para mostrar/ocultar campos)
  usaQtd = true,
  usaEuros = true,
  usaLitros = true,
  usaClientes = true,
}) {
  const [nome, setNome] = useState("");
  const [qtd, setQtd] = useState("");
  const [euros, setEuros] = useState("");
  const [litros, setLitros] = useState("");
  const [clientes, setClientes] = useState("");

  const [vendedoresSel, setVendedoresSel] = useState([]); // array de {value,label}

  // carregar valor atual quando abre
  useEffect(() => {
    if (!open) return;

    const v = value || null;

    setNome(v?.nome ?? "");
    setQtd(v?.qtd != null ? String(v.qtd) : "");
    setEuros(v?.euros != null ? String(v.euros) : "");
    setLitros(v?.litros != null ? String(v.litros) : "");
    setClientes(v?.clientes != null ? String(v.clientes) : "");

    const refs = Array.isArray(v?.vendedores) ? v.vendedores.map(String) : [];
    const opt = vendedoresOptions || [];
    setVendedoresSel(opt.filter((o) => refs.includes(String(o.value))));
  }, [open, value, vendedoresOptions]);

  const titulo = useMemo(() => {
    return value?.nome ? "Equipa (editar)" : "Equipa";
  }, [value]);

  const validarNumeros = () => {
    // permite vazio; se tiver valor, tem de ser número
    const campos = [
      { key: "Quantidade", val: qtd, ativo: usaQtd },
      { key: "Euros", val: euros, ativo: usaEuros },
      { key: "Litros", val: litros, ativo: usaLitros },
      { key: "Clientes", val: clientes, ativo: usaClientes },
    ];

    for (const c of campos) {
      if (!c.ativo) continue;
      const s = (c.val ?? "").toString().trim();
      if (!s) continue;

      const norm = s.replace(",", ".");
      if (!/^\d+(\.\d+)?$/.test(norm)) return `O campo "${c.key}" tem de ser numérico.`;
    }
    return null;
  };

  const handleApply = () => {
    const n = (nome ?? "").toString().trim();
    if (!n) return;

    const errNum = validarNumeros();
    if (errNum) return;

    const refs = (vendedoresSel || []).map((x) => String(x.value)).filter(Boolean);
    if (refs.length === 0) return;

    const out = {
      nome: n,
      qtd: usaQtd && (qtd ?? "").toString().trim() !== "" ? Number(qtd) : null,
      euros: usaEuros && (euros ?? "").toString().trim() !== "" ? Number(euros) : null,
      litros: usaLitros && (litros ?? "").toString().trim() !== "" ? Number(litros) : null,
      clientes: usaClientes && (clientes ?? "").toString().trim() !== "" ? Number(clientes) : null,
      vendedores: refs,
    };

    onApply(out);
  };

  const podeAplicar = (nome ?? "").toString().trim().length > 0 && (vendedoresSel || []).length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gerir Equipas</DialogTitle>

      <DialogContent dividers>
        <MDBox mb={2}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
            {titulo}
          </MDTypography>

          <MDTypography variant="caption" color="text">
            Define uma equipa e associa pelo menos um vendedor. (Por agora: 1 equipa apenas.)
          </MDTypography>
        </MDBox>

        <MDBox display="flex" flexWrap="wrap" gap={2}>
          <MDBox sx={{ minWidth: 250, flex: "1 1 250px" }}>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
              Nome da equipa
            </MDTypography>
            <TextField
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Outros"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                },
              }}
            />
          </MDBox>

          {usaQtd && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Quantidade (mín.)
              </MDTypography>
              <TextField
                value={qtd}
                onChange={(e) => setQtd(onlyNumberOrEmpty(e.target.value))}
                // inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    width: 90,
                  },
                }}
              />
            </MDBox>
          )}

          {usaEuros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Euros (mín.)
              </MDTypography>
              <TextField
                value={euros}
                onChange={(e) => setEuros(onlyNumberOrEmpty(e.target.value))}
                // inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    width: 90,
                  },
                }}
              />
            </MDBox>
          )}

          {usaLitros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Litros (mín.)
              </MDTypography>
              <TextField
                value={litros}
                onChange={(e) => setLitros(onlyNumberOrEmpty(e.target.value))}
                // inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    width: 90,
                  },
                }}
              />
            </MDBox>
          )}

          {usaClientes && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Clientes (mín.)
              </MDTypography>
              <TextField
                value={clientes}
                onChange={(e) => setClientes(onlyNumberOrEmpty(e.target.value))}
                // inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    width: 90,
                  },
                }}
              />
            </MDBox>
          )}
        </MDBox>

        <MDBox mt={2}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
            Vendedores (obrigatório)
          </MDTypography>

          <Autocomplete
            multiple
            options={vendedoresOptions || []}
            value={vendedoresSel}
            onChange={(_e, v) => setVendedoresSel(v || [])}
            getOptionLabel={(o) => o?.label ?? ""}
            isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Seleciona um ou mais vendedores..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            )}
          />

          {(vendedoresSel || []).length === 0 && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="error">
                Seleciona pelo menos um vendedor para a equipa.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton color="info" onClick={onClose}>
          Fechar
        </MDButton>

        <MDButton color="success" onClick={handleApply} disabled={!podeAplicar}>
          Aplicar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
